import { useState } from "react";

import { SystemProps, Box, css, Spinner } from "@storyofams/react-ui";
import { defineMessages, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { ProductSortKeys } from "shopify.sdk";
import { useAuth } from "~context/auth";
import { shopifySdk } from "~lib/shopify/client";
import { Button, ProductSlider } from "../../../../components";
import { useShopify } from "../../../../context";
import { showToast } from "../../../../lib";

type PopularProductsProps = {
  first?: boolean;
  block?: boolean;
  content: {
    title: string;
    component: string;
    anchor_id?: string;
    products_variants?: any;
    description?: string;
    link_label?: string;
    link_url?: any;
    products?: any;
    add_to_cart_button?: boolean;
    type: string;
    products_count: string;
    background: any;
  };
} & SystemProps;

const messages = defineMessages({
  addItemsFail:
    "Er ging iets fout bij het toevoegen van producten aan het winkelmandje!",
  addItems: "Voeg producten aan winkelmand toe",
});

export const PopularProducts = ({
  content,
  first,
  ...props
}: PopularProductsProps) => {
  const intl = useIntl();
  const {
    addItemToCart,
    updatingCart,
    toggleCartOverlay,
    customerCompanyId,
    isB2B,
  } = useShopify();
  const { getAccessToken } = useAuth();
  const [adding, setAdding] = useState(null);
  const { data: products, isLoading } = useQuery(
    ["products", isB2B, content],
    async () => {
      const products = content?.products_variants
        ? content.products_variants.items.map((item) => item.id)
        : content.products.item.map((item) => item.id);
      if (customerCompanyId && isB2B) {
        const token = await getAccessToken();
        return shopifySdk.productsByIdsWithContext({
          ids: products,
          buyer: {
            customerAccessToken: token,
            companyLocationId: customerCompanyId,
          },
        });
      }
      return shopifySdk.productsByIds({
        ids: products,
      });
    },

    { enabled: Boolean(content) }
  );
  const { data: popularProducts, isLoading: isPopularLoading } = useQuery(
    ["popularProducts", isB2B, content, customerCompanyId],
    async () => {
      if (customerCompanyId && isB2B) {
        const token = await getAccessToken();
        return shopifySdk.productsWithContext({
          first: 4,
          sortKey: ProductSortKeys.BestSelling,
          query: "available_for_sale:true",
          ...(customerCompanyId &&
            isB2B && {
              buyer: {
                customerAccessToken: token,
                companyLocationId: customerCompanyId,
              },
            }),
        });
      } else
        return shopifySdk.products({
          first: 4,
          sortKey: ProductSortKeys.BestSelling,
          query: "available_for_sale:true",
        });
    },

    {
      refetchOnWindowFocus: false,
    }
  );
  const getProducts = () => {
    if (content.component === "popular_products") {
      return popularProducts?.products?.edges?.map(({ node }) => node);
    }
    return products?.nodes || [];
  };
  return (
    <Box
      width="100%"
      overflow={"clip"}
      display={"flow-root"}
      css={{ "overflow-clip-margin": "content-box 12px;" }}
      pt={content.background && 5}
      // onClick={() => posthog.capture("Interaction with Popular Tab")}
    >
      {content.background && content.background.filename && (
        <Box
          top={-1}
          zIndex={-1}
          left={0}
          width="100%"
          position="absolute"
          height="750px"
          css={css({
            backgroundImage: `url(${content.background.filename})`,
            backgroundSize: "100% 100%",
          })}
        />
      )}
      {isLoading || isPopularLoading ? (
        <Spinner mt={2} />
      ) : (
        <ProductSlider
          title={content.title}
          description={content?.description}
          products={getProducts() as any}
          link={content.link_url}
          linkLabel={content.link_label}
        />
      )}

      {content?.add_to_cart_button && (
        <Button
          mt={[4, 6]}
          isLoading={updatingCart && adding}
          variant="primary"
          onClick={() => {
            setAdding(true);
            addItemToCart(
              !!content?.products?.[0]?.node
                ? content?.products?.map(({ node }) => ({
                    quantity: 1,
                    merchandiseId: node?.variants?.edges?.[0]?.node?.id,
                  }))
                : content?.products?.map((product) => ({
                    quantity: 1,
                    merchandiseId: product?.variants?.edges?.[0]?.node?.id,
                  })),
              () => {
                setAdding(false);
                toggleCartOverlay(true);
              }
            ).catch(() =>
              showToast(intl.formatMessage(messages.addItemsFail), "error")
            );
          }}
        >
          {intl.formatMessage(messages.addItems)}
        </Button>
      )}
    </Box>
  );
};
